import clsx from 'clsx';
import React from 'react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import TagsInput from '../TagsInput';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Input from '../Input';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { useFormik } from 'formik';
import { getEditDocumentSchema } from '../../views/DocumentLibrary/schema';
import { useApiClients } from '../../client/api/useApiClient';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { Documents } from '@EHSNavigator/backend';

interface EditDocumentModalProps {
  otherClasses?: string;
  handleClose: () => void;
  selectedDocument: Documents | null;
  state: any;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  otherClasses,
  handleClose,
  selectedDocument,
  state,
}) => {
  const editDocumentModalClasses = clsx(otherClasses);

  const { DocumentLibraryApi } = useApiClients();

  const formHandler = useFormik({
    initialValues: {
      id: selectedDocument?.id || '',
      name: selectedDocument?.name.includes('.')
        ? selectedDocument?.name.substring(
            0,
            selectedDocument?.name.lastIndexOf('.'),
          )
        : selectedDocument?.name,
      tags: selectedDocument?.tags || [],
    },
    enableReinitialize: true,
    validationSchema: getEditDocumentSchema(),
    onSubmit: async (values) => {
      try {
        const res = await DocumentLibraryApi.documentLibraryControllerUpdate({
          id: values?.id,
          updateDocumentLibraryRequestDto: {
            name: values?.name,
            tags: values?.tags,
          },
        });

        queryClient.setQueryData(
          [
            'all-documents',
            state?.page,
            state?.search,
            state?.documentType,
            state?.limit,
          ],
          (oldData: any) => {
            const updatedInvestigationQuestions = {
              ...oldData,
              documents: oldData?.documents?.map((node: any) =>
                node?.id === values?.id ? res?.data?.document : node,
              ),
            };
            return updatedInvestigationQuestions;
          },
        );
        toast.success(res?.data?.message);
        handleClose();
        formHandler.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const hasChanges = formHandler.dirty;

  return (
    <div className={editDocumentModalClasses} data-testid="edit-document-modal">
      <div className="flex flex-col items-center gap-2.5 text-center">
        <EditIcon className="[&>path]:fill-brand-byzantine h-85 w-85" />
        <h4 className="text-brand-byzantine capitalize">
          Edit Document Details
        </h4>
        <p className="font-inter-variable text-brand-byzantine -tracking-text-area text-base leading-5 font-normal lg:text-lg">
          Update your document information below. Ensure all required fields
          (marked with *) are filled before saving.
        </p>
      </div>
      <form
        action="#"
        onSubmit={formHandler?.handleSubmit}
        className="mt-12 flex flex-col gap-6"
      >
        <Input
          onBlur={formHandler?.handleBlur}
          label="File Name*"
          errorMessage={
            formHandler?.touched.name && formHandler?.errors.name
              ? formHandler?.errors.name
              : ''
          }
          disabled={formHandler?.isSubmitting}
          value={formHandler?.values.name || ''}
          type="text"
          placeholder="Enter the file name"
          onChange={formHandler?.handleChange}
          name="name"
          otherClasses={{ container: 'w-full' }}
        />
        <TagsInput
          disabled={formHandler?.isSubmitting}
          name="tags"
          value={formHandler?.values?.tags}
          onChange={(e) => formHandler?.setFieldValue('tags', e, true)}
          errorMessage={
            formHandler?.errors?.tags
              ? Array.isArray(formHandler.errors.tags)
                ? formHandler.errors.tags.join(', ')
                : formHandler.errors.tags
              : ''
          }
          otherClasses={{
            container: 'w-full',
            label: 'flex items-center gap-1 relative w-full md:w-fit',
          }}
          label={
            <>
              Tags*
              <button type="button" className="group inline-block md:relative">
                <WarningIcon className="h-5 w-5" />
                <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden w-full -translate-x-2/4 rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block md:min-w-44">
                  Type and click enter to add your first option.
                </p>
              </button>
            </>
          }
          handleBlur={formHandler?.handleBlur}
          placeholder=" Add a tag..."
        />

        <div className="mt-4 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6">
          <Button
            variant="secondary"
            type="button"
            size={useButtonSizeSmall()}
            onClick={() => handleClose()}
            disable={formHandler?.isSubmitting}
            otherClasses="border-transparent text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={formHandler?.isSubmitting}
            disable={formHandler?.isSubmitting || !hasChanges}
            size={useButtonSizeSmall()}
            otherClasses=""
          >
            Edit Document
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditDocumentModal;